import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Scaffold } from "../Scaffold";
import { State } from "../redux/types";
import { useSelector } from "react-redux";

// Type for gift card object
interface GiftCardItem {
  cardNumber?: string;
  expirationDate?: string;
  cvv?: string;
  balance?: string;
  [key: string]: unknown;
}

// Format card number with spaces (0000 0000 0000 0000)
const formatCardNumber = (cardNumber?: string): string => {
  if (!cardNumber) { return "N/A"; }
  // Remove any existing spaces and format
  const cleaned = cardNumber.replaceAll(/\s/g, "");
  return cleaned.match(/.{1,4}/g)?.join(" ") ?? cardNumber;
};

export const GiftCard = () => {
  const navigate = useNavigate();
  const advanceVendorResult = useSelector((state: State) => state.advanceResult);

  // Get gift cards array from customer data

  const giftCards: GiftCardItem[] = advanceVendorResult?.customer?.giftCards ?? [];

  const handleBackToResults = () => {
    navigate("/advanceresult");
  };

  return (
    <Scaffold navBar>
      <Box sx={{ 
        minHeight: "100vh",
        width: "100%",
        display: "flex", 
        flexDirection: "column", 
        position: "relative",
        overflowX: "hidden",
        overflowY: "auto"
      }}>
        {/* Back Button */}
        <Box sx={{ 
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 1
        }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToResults}
            sx={{ 
              color: "text.secondary",
              textTransform: "none",
              fontSize: "0.875rem"
            }}
          >
            BACK TO RESULTS
          </Button>
        </Box>

        {/* Main Content Container - Centered Horizontally */}
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          width: "100%",
          pt: 8,
          px: 3
        }}>
          {/* Gift Card Title - Centered */}
          <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold", textAlign: "center" }}>
            Zaxby&apos;s Gift Card
          </Typography>

          {/* Customer Account Card */}
          <Card 
            sx={{ 
              border: "3px solid rgba(0,0,0,0.1)", 
              borderRadius: 2,
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              minWidth: "400px",
              maxWidth: "600px"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  fontWeight: "bold",
                  color: "#333"
                }}
              >
                Customer Account
              </Typography>

              {giftCards.length > 0 ? (
                <Stack spacing={2} divider={<Divider flexItem />}>
                  {giftCards.map((card, cardIdx) => (
                    <Box
                      key={`${card.cardNumber ?? "na"}-${card.expirationDate ?? ""}-${card.balance ?? ""}`}
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: 100 }}>
                        Gift Card {cardIdx + 1}:
                      </Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2">
                          {formatCardNumber(card.cardNumber)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {card.expirationDate ?? "N/A"} cvv {card.cvv ?? "N/A"}
                        </Typography>
                        {card.balance && (
                          <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                            Balance: ${card.balance}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No gift cards on file
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Scaffold>
  );
};
